import { useCallback, useEffect, useRef, useState } from "react";
import logibudLogo from "../assets/logo/logibud_primary_darkblue.svg";
import shopifyLogo from "../assets/integrations/shopify-logo.svg";

declare global {
  interface Window {
    shopify?: { idToken: () => Promise<string> };
  }
}

type AppSession = {
  shopDomain: string;
  connectionId: string;
  linked: boolean;
  status: string;
  manageUrl?: string;
  importEnabled?: boolean;
  webhookStatus?: "active" | "error" | "disabled";
  lastWebhookAt?: string;
  lastSuccessfulSyncAt?: string;
  lastErrorCode?: string;
};

const apiBaseUrl =
  process.env.REACT_APP_CLOUD_API_URL || "http://localhost:8080";

const formatTimestamp = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("en", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "No orders yet";

let appBridgeLoadPromise: Promise<void> | undefined;

const loadAppBridge = async () => {
  if (window.shopify) return;
  if (!appBridgeLoadPromise) {
    appBridgeLoadPromise = (async () => {
      const configResponse = await fetch(`${apiBaseUrl}/shopify/app/config`);
      if (!configResponse.ok) {
        throw new Error("Could not load Shopify app configuration");
      }
      const config = await configResponse.json();
      const meta = document.createElement("meta");
      meta.name = "shopify-api-key";
      meta.content = config.clientId;
      document.head.prepend(meta);
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdn.shopify.com/shopifycloud/app-bridge.js";
        // Dynamically-created scripts are async by default. App Bridge rejects
        // both async loading and any position other than the first script tag.
        script.async = false;
        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error("Could not load Shopify App Bridge"));
        const firstScript = document.scripts.item(0);
        if (firstScript?.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          document.head.appendChild(script);
        }
      });
    })().catch((error) => {
      appBridgeLoadPromise = undefined;
      throw error;
    });
  }
  return appBridgeLoadPromise;
};

const shopifyRequest = async (path: string) => {
  await loadAppBridge();
  if (!window.shopify) throw new Error("Open Logibud from Shopify admin");
  const request = async () => {
    const token = await window.shopify!.idToken();
    return fetch(`${apiBaseUrl}${path}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  };
  let response = await request();
  if (response.status === 401) response = await request();
  if (!response.ok)
    throw new Error("Shopify could not authenticate this session");
  return response.json();
};

const ShopifyAppHomePage = () => {
  const [session, setSession] = useState<AppSession>();
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [isLinkWindowOpen, setIsLinkWindowOpen] = useState(false);
  const popup = useRef<Window | null>(null);
  const importRunning = session?.importEnabled !== false;

  const refresh = useCallback(async () => {
    try {
      const data = await shopifyRequest("/shopify/app/session");
      setSession(data.data);
      setError(undefined);
    } catch (requestError: any) {
      setError(requestError?.message || "Could not open Logibud");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const handleLinkComplete = (event: MessageEvent) => {
      if (
        event.origin === window.location.origin &&
        event.data?.type === "logibud:shopify-linked"
      ) {
        setIsLinkWindowOpen(false);
        popup.current = null;
        void refresh();
      }
    };
    window.addEventListener("message", handleLinkComplete);
    return () => window.removeEventListener("message", handleLinkComplete);
  }, [refresh]);

  useEffect(() => {
    if (!isLinkWindowOpen || session?.linked) return;
    const timer = window.setInterval(() => {
      if (popup.current?.closed) {
        popup.current = null;
        setIsLinkWindowOpen(false);
      }
      void refresh();
    }, 2000);
    return () => window.clearInterval(timer);
  }, [isLinkWindowOpen, refresh, session?.linked]);

  const connect = async () => {
    setIsLoading(true);
    try {
      const result = await shopifyRequest("/shopify/app/workspace-link");
      popup.current = window.open(
        result.data.authorizationUrl,
        "logibud-shopify-link",
        "popup,width=720,height=780",
      );
      if (!popup.current) throw new Error("Allow pop-ups to connect Logibud");
      setIsLinkWindowOpen(true);
    } catch (requestError: any) {
      setError(requestError?.message || "Could not start Logibud connection");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 px-5 py-8 text-gray-900">
      <section className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        <div className="flex items-center justify-between gap-6 border-b border-gray-100 pb-6">
          <img alt="Logibud" className="h-9 w-auto" src={logibudLogo} />
          <img alt="Shopify" className="h-10 w-auto" src={shopifyLogo} />
        </div>
        <div className="py-8">
          <h1 className="text-2xl font-semibold">Connect Shopify to Logibud</h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            Turn paid Shopify orders that require delivery into transport drafts
            in Logibud.
          </p>

          {isLoading && !session ? (
            <p className="mt-6 rounded-md bg-gray-100 p-4 text-sm text-gray-700">
              Checking your Shopify installation…
            </p>
          ) : error ? (
            <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
              <button
                className="ml-3 font-medium underline"
                onClick={() => void refresh()}
              >
                Try again
              </button>
            </div>
          ) : session?.linked ? (
            <div className="mt-6 overflow-hidden rounded-md border border-gray-200">
              <div className="border-b border-gray-200 bg-gray-50 p-5">
                <p className="font-medium text-gray-900">
                  Shopify is connected to Logibud
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  {session.shopDomain}
                </p>
              </div>
              <dl className="divide-y divide-gray-100 px-5">
                <div className="grid gap-1 py-3 text-sm sm:grid-cols-[10rem_1fr] sm:gap-4">
                  <dt className="text-gray-500">Import status</dt>
                  <dd className="flex items-center gap-2 font-medium text-gray-900">
                    <span
                      aria-hidden="true"
                      className={`h-2 w-2 rounded-full ${
                        session.lastErrorCode ||
                        session.webhookStatus === "error"
                          ? "bg-red-500"
                          : importRunning
                            ? "bg-green-500"
                            : "bg-gray-400"
                      }`}
                    />
                    {session.lastErrorCode || session.webhookStatus === "error"
                      ? "Needs attention"
                      : importRunning
                        ? "Running"
                        : "Paused"}
                  </dd>
                </div>
                <div className="grid gap-1 py-3 text-sm sm:grid-cols-[10rem_1fr] sm:gap-4">
                  <dt className="text-gray-500">Last order received</dt>
                  <dd className="text-gray-900">
                    {formatTimestamp(session.lastWebhookAt)}
                  </dd>
                </div>
                <div className="grid gap-1 py-3 text-sm sm:grid-cols-[10rem_1fr] sm:gap-4">
                  <dt className="text-gray-500">Last successful import</dt>
                  <dd className="text-gray-900">
                    {formatTimestamp(session.lastSuccessfulSyncAt)}
                  </dd>
                </div>
              </dl>
              <div className="flex flex-wrap items-center gap-4 border-t border-gray-200 p-5">
                <a
                  className="inline-flex rounded-md bg-lgb-primary px-4 py-2.5 text-sm font-medium text-white"
                  href={session.manageUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  Manage in Logibud
                </a>
                <p className="text-sm text-gray-600">
                  Pause imports, review product mappings and resolve errors in
                  Logibud.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-md border border-gray-200 p-5">
              <p className="text-sm text-gray-700">
                Sign in to Logibud and choose which workspace should receive
                orders from {session?.shopDomain}.
              </p>
              <button
                className="mt-5 rounded-md bg-lgb-primary px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
                disabled={isLoading}
                onClick={() => void connect()}
              >
                Connect Logibud workspace
              </button>
            </div>
          )}
        </div>
        <footer className="flex flex-wrap gap-x-5 gap-y-2 border-t border-gray-100 pt-5 text-sm">
          <a
            className="text-gray-600 underline hover:text-gray-900"
            href="https://www.logibud.no/juridisk/personvern"
            rel="noreferrer"
            target="_blank"
          >
            Privacy policy
          </a>
          <a
            className="text-gray-600 underline hover:text-gray-900"
            href="https://www.logibud.no/juridisk/personvernvalg"
            rel="noreferrer"
            target="_blank"
          >
            Data requests and deletion
          </a>
          <a
            className="text-gray-600 underline hover:text-gray-900"
            href="https://www.logibud.no/hjelp"
            rel="noreferrer"
            target="_blank"
          >
            Support
          </a>
        </footer>
      </section>
    </main>
  );
};

export default ShopifyAppHomePage;
